import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AuthShell from './AuthShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { resetPassword } from '@/lib/api';

export default function Reset() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (!token) {
      setError('Reset link is missing its token.');
      return;
    }
    setPending(true);
    try {
      const fd = new FormData(event.currentTarget);
      const password = String(fd.get('password') ?? '');
      const confirm = String(fd.get('confirm') ?? '');
      if (password !== confirm) {
        setError('Passwords do not match.');
        return;
      }
      await resetPassword(token, password);
      setDone(true);
      setTimeout(() => navigate('/auth/sign-in', { replace: true }), 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reset password');
    } finally {
      setPending(false);
    }
  }

  return (
    <AuthShell
      stamp="Set a new password"
      title="Reset password"
      subtitle="Type a new password and confirm it. The reset link expires in an hour."
      footer={
        <Link to="/auth/sign-in" className="text-primary hover:underline">
          Back to sign in
        </Link>
      }
    >
      {done ? (
        <p className="text-sm" data-testid="reset-done">
          Password updated. Redirecting to sign-in…
        </p>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4" data-testid="reset-form">
          <div className="space-y-2">
            <Label htmlFor="password" className="font-mono text-[11px] uppercase tracking-wider">
              New password
            </Label>
            <Input
              id="password"
              name="password"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              autoFocus
              className="h-11 text-base"
              data-testid="password"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm" className="font-mono text-[11px] uppercase tracking-wider">
              Confirm
            </Label>
            <Input
              id="confirm"
              name="confirm"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              className="h-11 text-base"
              data-testid="confirm"
            />
          </div>
          {error ? (
            <p className="text-sm text-warning font-medium" role="alert">
              {error}
            </p>
          ) : null}
          <Button type="submit" className="w-full h-11 text-base" disabled={pending} data-testid="submit">
            {pending ? 'Saving…' : 'Save password'}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
